import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/shared/api/client';
import enParallele from './enParallele';

/**
 * LES PAGES D'UN GABARIT, EN IMAGES, PRÊTES À RECEVOIR DES CHAMPS.
 *
 * L'atelier pose les champs du mandat en glissant-déposant SUR le document. Le
 * `<iframe>` d'aperçu ne peut pas servir à ça : son contenu est rendu par le visionneur
 * PDF natif du navigateur, et le DOM React n'a AUCUNE prise dessus — ni coordonnées, ni
 * événements souris. Il faut donc une image, et une image dont on connaît l'échelle.
 *
 * ⚠️ POURQUOI PASSER PAR UN BLOB PLUTÔT QUE PAR `<img src="/api/…">`
 *
 * `shared/api/client.js` pose le jeton dans un en-tête `Authorization`. Un `<img>` fait
 * sa propre requête, sans intercepteur et sans en-tête : il recevrait un 401 et
 * afficherait une icône cassée. La tentation inverse — ouvrir la route pour que l'`<img>`
 * puisse la charger — servirait le document contractuel d'un client à qui sait deviner
 * un identifiant. On télécharge donc en blob, et on fabrique une URL d'objet.
 *
 * ⚠️ ET POURQUOI CHAQUE URL EST RÉVOQUÉE
 *
 * Une URL d'objet vit jusqu'à ce qu'on la révoque, même quand plus rien ne la pointe. Le
 * défaut est déjà commenté dans l'écran (l.87-89) pour UN aperçu ; ici il serait
 * multiplié par le nombre de pages, à chaque changement de gabarit. Un gabarit de dix
 * pages ouvert cinq fois laisserait cinquante images en mémoire.
 *
 * ⚠️ ET POURQUOI IL N'Y A NULLE PART DE `?t=Date.now()`
 *
 * La route pose un `ETag` et un `Cache-Control: private, max-age=86400, immutable`. Un
 * paramètre anti-cache l'annulerait, et chaque affichage repaierait les 74 ms de
 * rastérisation par page mesurées sur le VPS. L'image ne peut pas avoir changé : l'ETag
 * porte l'identifiant GridFS du fichier, qui est immuable.
 */

// Le zoom demandé au serveur. 2 rend une A4 en 1191 x 1684 px pour 273 ko sur le gabarit
// réel de David : assez fin pour lire les libellés sous lesquels on pose un champ, et
// assez léger pour empiler les pages. Le serveur borne à 3 de toute façon.
const ZOOM = 2;

export default function usePagesDuGabarit(gabaritId) {
  const [pages, setPages] = useState([]);
  const [images, setImages] = useState({});
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  // Les URL vivantes, hors du cycle de rendu : on doit pouvoir les révoquer depuis le
  // nettoyage d'effet, qui ne voit pas l'état courant.
  const urls = useRef([]);

  const oublier = useCallback(() => {
    urls.current.forEach((url) => URL.revokeObjectURL(url));
    urls.current = [];
  }, []);

  useEffect(() => {
    if (!gabaritId) { oublier(); setPages([]); setImages({}); return undefined; }

    // `vivant` coupe l'écriture d'état d'une réponse arrivée après un changement de
    // gabarit : sans lui, cliquer vite sur deux gabarits affiche les pages du premier.
    let vivant = true;
    setChargement(true);
    setErreur(null);
    oublier();
    setImages({});

    (async () => {
      try {
        const { data } = await api.get(`/admin/mandat-templates/${gabaritId}/pages`);
        if (!vivant) return;
        setPages(data);

        // ⚠️ TROIS À LA FOIS, ET CHAQUE PAGE S'AFFICHE DÈS QU'ELLE ARRIVE.
        //
        // Elles se chargeaient une par une, chacune attendant la précédente : sur un
        // mandat de quatre pages, quatre fois 138 ms au lieu d'une. Le réflexe est
        // `Promise.all`, et il échangerait une attente contre une saturation — chaque
        // image est une rastérisation serveur, ~100 ms de processeur sur la même
        // machine. Voir `enParallele` : la concurrence est bornée, et le rendu au fil de
        // l'eau garde ce que la version en série avait de bon.
        await enParallele(data, (page) => api.get(
          `/admin/mandat-templates/${gabaritId}/pages/${page.numero}/image`,
          { params: { zoom: ZOOM }, responseType: 'blob' }), {
          arreter: () => !vivant,
          surChaque: (page, reponse) => {
            const url = URL.createObjectURL(
              new Blob([reponse.data], { type: 'image/png' }));
            urls.current.push(url);
            setImages((deja) => ({ ...deja, [page.numero]: url }));
          },
        });
      } catch (e) {
        if (vivant) setErreur(e.response?.data?.detail || "Le plan n'a pas pu être chargé");
      } finally {
        if (vivant) setChargement(false);
      }
    })();

    return () => { vivant = false; };
  }, [gabaritId, oublier]);

  // Le démontage du composant, distinct du changement de gabarit : quitter l'écran doit
  // rendre la mémoire, pas seulement en changer.
  useEffect(() => oublier, [oublier]);

  return { pages, images, chargement, erreur };
}
